"use client";

import {
  Tooltip as AriaTooltip,
  TooltipProps as AriaTooltipProps,
  TooltipTrigger as AriaTooltipTrigger,
  Button,
  OverlayArrow,
  TooltipTriggerComponentProps,
} from "react-aria-components";

import { useRef, useState } from "react";
import { useTheme } from "./ThemeProvider";

import "./Dialog.css";
import "./Tooltip.css";
import { csx } from "../utils";

export interface TooltipProps extends Omit<AriaTooltipProps, "children"> {
  children: React.ReactNode;
  arrow?: boolean;
}

export function Tooltip({ children, arrow, ...props }: TooltipProps) {
  const { style, className } = useTheme();

  return (
    <AriaTooltip
      {...props}
      className={csx("proton__Tooltip", "proton-Dialog", className)}
      style={style as React.CSSProperties}
    >
      {arrow && (
        <OverlayArrow className={csx("proton__TooltipArrow", className)}>
          <svg width={8} height={8} viewBox="0 0 8 8">
            <path d="M0 0 L4 4 L8 0" />
          </svg>
        </OverlayArrow>
      )}

      {children}
    </AriaTooltip>
  );
}

export interface TooltipTriggerProps
  extends TooltipProps,
    TooltipTriggerComponentProps {
  children: React.ReactNode;
  /**
   * The content to display in the tooltip. Zero-length titles string are never displayed.
   */
  content: string | React.ReactNode;
  /**
   * Whether to disable the trigger click feature.
   */
  disableTriggerClick?: boolean;
}

/**
 * Proton's TooltipTrigger component deviates from the Aria TooltipTrigger in that on mobile,
 * the trigger also opens the tooltip on click as well as hover & focus. This is to improve
 * accessibility and usability on mobile.
 *
 * If you wish to disable the trigger click feature, you can set the `disableTriggerClick` prop.
 * This is useful when using an info only Tooltip that might wrap a clickable element.
 */

export function TooltipTrigger({
  children,
  // trigger props
  delay = 500,
  closeDelay = 500,
  isDisabled,
  defaultOpen,
  onOpenChange,
  disableTriggerClick,
  // tooltip props
  content,
  ...tooltipProps
}: TooltipTriggerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerClickRef = useRef<boolean>(false);
  const hasHovered = useRef<boolean>(false);

  if (content === "") return null;

  return (
    <AriaTooltipTrigger
      delay={delay}
      closeDelay={closeDelay}
      isDisabled={isDisabled}
      isOpen={isOpen}
      defaultOpen={defaultOpen}
      onOpenChange={(openState) => {
        // When opened via hover, we want to disable the trigger click feature as it causes
        // the tooltip state to get out of sync with the trigger state.
        if (openState && !triggerClickRef.current) hasHovered.current = true;

        if (onOpenChange) onOpenChange(openState);

        // If the tooltip is clicked, it manually calls setIsOpen, so we don't want to call
        // it again here.
        if (!triggerClickRef.current) setIsOpen(openState);
        triggerClickRef.current = false;
      }}
    >
      {disableTriggerClick ? (
        <span className="proton__TooltipTrigger" aria-label="Show tooltip">
          {children}
        </span>
      ) : (
        <Button
          className="proton__TooltipTrigger"
          aria-label="Show tooltip"
          onPress={() => {
            if (disableTriggerClick || isDisabled) return;

            // Once hovered it's safe to assume the user isn't trying to use via a touch device,
            // so we can safely disable the trigger click feature.
            if (hasHovered.current) return;

            triggerClickRef.current = true;
            setIsOpen(!isOpen);
          }}
        >
          {children}
        </Button>
      )}
      <Tooltip {...tooltipProps}>{content}</Tooltip>
    </AriaTooltipTrigger>
  );
}
